import { useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ctxPluto } from '@/contexts/PlutoContext';
import { Popup } from '@/components/common/Popup';
import { ctxUser } from '@/contexts/UserContext';
import { useTargetSelector } from '@/contexts/useTargetSelector';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

import { BottomSheet } from '@/components/common/BottomSheet';
import { HeaderNameAndIcons } from '@/components/common/HeaderNameAndIcons';
import TargetTrackingPanel from './TargetTrackingPanel';
import TrackingTargetSelector from './TrackingTargetSelector';
import { trackedTargetLimit } from '@/tmp/TargetService';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';

import '@/components/common/Icons.css';
import './TrackingPopup.css';
import Box from '@mui/material/Box';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import FloatingBottomButton from '@/components/common/FloatingBottomButton';

// target selector popup gorgetheto body resze
function Body({ trackingLimit, closeTargetSelector }: { trackingLimit: number; closeTargetSelector: () => void }) {
  if (isPhoneOrHighScreen()) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TrackingTargetSelector trackingLimit={trackingLimit} closeTargetSelector={closeTargetSelector} />
      </Box>
    );
  }

  return (
    <div className="tracking-body-target" data-qa-element-id="multi_element_selector_panel">
      <TrackingTargetSelector trackingLimit={trackingLimit} closeTargetSelector={closeTargetSelector} />
    </div>
  );
}

function TargetSelectorPopup({ trackingLimit }: { trackingLimit: number }) {
  const ctx = useContext(ctxPluto);
  const { closeTargetSelector } = useTargetSelector();
  const { t } = useTranslation();
  const [initialSelection] = useState(ctx?.selectedTargets ?? []);

  const initialTargetIds = initialSelection.map((target) => target.id).sort((a, b) => a - b);
  const selectedTargetIds = (ctx?.selectedTargets ?? []).map((target) => target.id).sort((a, b) => a - b);
  const isSelectionChanged =
    initialTargetIds.length !== selectedTargetIds.length ||
    initialTargetIds.some((id, index) => id !== selectedTargetIds[index]);

  async function onClose() {
    closeTargetSelector();
    ctx?.setFocusedTarget(null);
  }

  return (
    <Popup
      popupTitle={t('mainMenu.tracking')}
      header={undefined}
      body={<Body trackingLimit={trackingLimit} closeTargetSelector={closeTargetSelector} />}
      footer={
        isSelectionChanged ? (
          <FloatingBottomButton
            icon={<MapOutlinedIcon />}
            variant="circular"
            onAction={() => onClose()}
            hasBorder={true}
            bottom={12}
            position="absolute"
          />
        ) : undefined
      }
      closePopup={onClose}
      isFullScr={true}
      leftText={true}
    />
  );
}

// popup, a kivalasztott target listat modositja
export default function TrackingPopup() {
  const ctx = useContext(ctxPluto);
  const userCtx = useContext(ctxUser);
  const { closeTargetSelector, isTargetSelectorOpen } = useTargetSelector();
  const [trackingLimit, setTrackingLimit] = useState<number | undefined>(undefined);
  const target = ctx?.fullTargets?.find((t) => t.target.id === ctx?.popupTargetId);

  useEffect(() => {
    async function getTrackingLimit() {
      try {
        const response = await trackedTargetLimit();
        if (response.status === 200) {
          if (!response.data) {
            console.debug('tracked target limit is missing');
            setTrackingLimit(50); // FIXME put sane defaults into separate file
          } else {
            setTrackingLimit(response.data);
          }
        }
      } catch (error) {
        rethrowIfNotClientException(error);
      }
    }
    if (userCtx?.isToken()) {
      return;
    }
    void getTrackingLimit();
  }, [userCtx]);

  useEffect(() => {
    ctx?.setMode('target'); // show selectedTargets on the map
  }, [ctx]);

  useEffect(() => {
    if (!target) ctx?.setPopupTargetId(undefined);
  }, [target]);

  const effectiveTrackingLimit = userCtx?.isToken() ? 1 : trackingLimit;
  const body = effectiveTrackingLimit ? (
    <Body trackingLimit={effectiveTrackingLimit} closeTargetSelector={closeTargetSelector} />
  ) : (
    <></>
  );

  // a header is scrollozodjon es a body is
  // akkor kellene a header max height ?
  return isPhoneOrHighScreen() ? (
    <>
      {isTargetSelectorOpen && effectiveTrackingLimit && <TargetSelectorPopup trackingLimit={effectiveTrackingLimit} />}
      {ctx?.popupTargetId && target && (
        <BottomSheet
          key={ctx.popupTargetId}
          header={
            <HeaderNameAndIcons
              target={target.target}
              onClose={() => ctx?.setPopupTargetId(undefined)}
              onReload={() => ctx?.setImmediateLoadTargetId(ctx?.popupTargetId)}
              hasTargetMetadataId={target.hasTachographMetadataId}
            />
          }
          setBottomSheetUp={ctx.setIsBottomSheetOpen}
        >
          <TargetTrackingPanel />
        </BottomSheet>
      )}
    </>
  ) : (
    <>
      <div className="tracking-panel">{body}</div>
    </>
  );
}
